import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const LAST_ACTIVE_KEY = 'mblearn_last_active_at';
const INACTIVITY_THRESHOLD_MS = 12 * 60 * 60 * 1000; // 12h

/**
 * Keys safe to clear on inactivity refresh.
 * Whitelist approach — anything not listed is preserved.
 *
 * NEVER cleared: sb-* auth tokens, language, mblearn-text-scale,
 * tts-preferences, notification-sound, mblearn_pending_consent,
 * studyTime_*, app-build-version, subscription_cache, pending_operations,
 * exam-recovery-session, *_backup, cached_progress_* (may hold unsynced
 * local progress), service worker / Cache Storage, sessionStorage.
 */
const EXACT_KEYS_TO_CLEAR = [
  'app-complete-state',
  'app-state-timestamp',
];

const PREFIXES_TO_CLEAR = [
  'cached_questions_',
  'cached_study_cards_',
  'useStateRestoration_', // temporary state restoration cache
];

function clearStaleLocalStorage(): string[] {
  const cleared: string[] = [];
  try {
    // Exact keys
    for (const k of EXACT_KEYS_TO_CLEAR) {
      if (localStorage.getItem(k) !== null) {
        localStorage.removeItem(k);
        cleared.push(k);
      }
    }
    // Prefixed keys — iterate snapshot of keys
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) keys.push(k);
    }
    for (const k of keys) {
      if (PREFIXES_TO_CLEAR.some((p) => k.startsWith(p))) {
        localStorage.removeItem(k);
        cleared.push(k);
      }
    }
  } catch (err) {
    console.warn('[InactivityRefresh] localStorage clear error:', err);
  }
  return cleared;
}

let refreshInFlight = false;

export function useInactivityCacheRefresh() {
  const queryClient = useQueryClient();
  const slowToastTimer = useRef<number | null>(null);

  useEffect(() => {
    const updateLastActive = () => {
      try {
        localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
      } catch {}
    };

    const maybeRefresh = async () => {
      if (refreshInFlight) return;
      let last = 0;
      try {
        last = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0', 10) || 0;
      } catch {}

      const now = Date.now();
      // First-ever visit: just stamp and exit.
      if (!last) {
        updateLastActive();
        return;
      }

      const elapsed = now - last;
      if (elapsed < INACTIVITY_THRESHOLD_MS) {
        updateLastActive();
        return;
      }

      refreshInFlight = true;
      const startedAt = performance.now();
      console.log(
        `[InactivityRefresh] Inactive for ${(elapsed / 1000 / 60 / 60).toFixed(2)}h — safe refresh`
      );

      // Show "syncing" toast only if it takes >800ms
      slowToastTimer.current = window.setTimeout(() => {
        toast.loading('Sincronizando contenido reciente...', {
          id: 'inactivity-refresh',
        });
      }, 800);

      try {
        // 1. Validate session (read-only — never signOut here).
        //    If invalid/expired, existing auth flow handles it naturally.
        await supabase.auth.getSession();

        // 2. Clear approved stale local caches.
        const cleared = clearStaleLocalStorage();
        console.log('[InactivityRefresh] Cleared keys:', cleared);

        // 3. Invalidate React Query so stale data is refetched lazily.
        await queryClient.invalidateQueries();

        updateLastActive();

        if (slowToastTimer.current) {
          clearTimeout(slowToastTimer.current);
          slowToastTimer.current = null;
        }
        const elapsedMs = performance.now() - startedAt;
        if (elapsedMs > 800) {
          toast.success('Contenido actualizado', { id: 'inactivity-refresh' });
        } else {
          toast.dismiss('inactivity-refresh');
        }
      } catch (err) {
        console.warn('[InactivityRefresh] error:', err);
        if (slowToastTimer.current) {
          clearTimeout(slowToastTimer.current);
          slowToastTimer.current = null;
        }
        toast.dismiss('inactivity-refresh');
      } finally {
        refreshInFlight = false;
      }
    };

    // Startup
    maybeRefresh();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    };
    const onFocus = () => maybeRefresh();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      if (slowToastTimer.current) clearTimeout(slowToastTimer.current);
    };
  }, [queryClient]);
}
